// packages/web/src/components/account/recharge-flow.tsx
/**
 * RechargeFlow — package selection + payment channel + order polling.
 *
 * 19BN Phase 4 skeleton: creates a pending order via /recharge/create-order,
 * polls status until 'paid'. Real payment provider integration (QR codes etc)
 * lands when merchant keys are provisioned. For now shows "pending" + a note.
 *
 * Spec: docs/architecture/account-system-design.md §2.3, §9.4
 */

import { useState, useEffect } from 'react';
import { useT } from '../../i18n/use-t.js';
import { useStore } from '../../lib/store/index.js';
import { rechargeApi } from '../../api/recharge.js';
import { useNotify } from '../../lib/hooks/use-notify.js';

interface Package {
  key: string;
  label: string;
  priceFen: number;
  credits: number;
  perks: string | null;
}

const PACKAGES: Package[] = [
  { key: 'experience', label: '体验包', priceFen: 900, credits: 900, perks: null },
  { key: 'standard', label: '标准包', priceFen: 3900, credits: 3900, perks: '+1 月 AI 评估' },
  { key: 'semester', label: '学期包', priceFen: 12800, credits: 12800, perks: '+3 月 AI 评估 + 10GB 云存储' },
];

const CHANNELS = [
  { key: 'alipay', label: '支付宝', color: '#1677ff', disabled: false },
  { key: 'wechat', label: '微信支付', color: '#07c160', disabled: false },
  { key: 'stripe', label: 'Stripe', color: '#635bff', disabled: true },
] as const;

export function RechargeFlow() {
  const t = useT();
  const refreshBalance = useStore((s) => s.refreshBalance);
  const notify = useNotify();
  const [selectedPkg, setSelectedPkg] = useState('standard');
  const [selectedChannel, setSelectedChannel] = useState('wechat');
  const [orderId, setOrderId] = useState<string | null>(null);
  const [orderStatus, setOrderStatus] = useState<string>('idle');
  const [error, setError] = useState<string | null>(null);

  // Poll order status.
  useEffect(() => {
    if (!orderId || orderStatus === 'paid' || orderStatus === 'idle') return;
    const poll = async () => {
      try {
        // 19BN Phase D D2: poll via the unified client. 401 → null (session
        // expired): stop the loop by treating it as terminal. Other errors
        // retry on the next tick (3s interval) as before.
        const data = await rechargeApi.getOrder(orderId);
        if (data === null) {
          setOrderStatus('idle');
          setOrderId(null);
          return;
        }
        setOrderStatus(data.status);
        if (data.status === 'paid') {
          void refreshBalance();
          // 19BN Phase D D6: notify recharge success via the unified useNotify
          // hook (was a direct pushNotification call — useNotify also emits the
          // EventBus 'notification:new' event so the toast bridge fires).
          notify({ source: 'account', level: 'success', title: '充值成功', message: '点数已到账', persistent: true });
        }
      } catch { /* retry next tick */ }
    };
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [orderId, orderStatus, refreshBalance, notify]);

  async function createOrder() {
    setError(null);
    setOrderStatus('creating');
    try {
      // 19BN Phase D D2: create-order via the unified client. The client
      // throws IngestApiError on non-2xx (incl. 400 invalid package/channel,
      // 401 session expired), which we surface as a localized message below.
      const data = await rechargeApi.createOrder(selectedPkg, selectedChannel);
      setOrderId(data.orderId);
      setOrderStatus('pending');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Order creation failed');
      setOrderStatus('idle');
    }
  }

  if (orderStatus === 'paid') {
    return (
      <div style={{ textAlign: 'center', padding: 'var(--space-4xl)' }}>
        <div style={{ fontSize: '48px', color: 'var(--color-success-deep)', marginBottom: 'var(--space-lg)' }}>✓</div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-2xl)', fontWeight: 600, color: 'var(--color-ink)' }}>
          {t('account.recharge')} 成功
        </div>
        <button
          onClick={() => { setOrderStatus('idle'); setOrderId(null); }}
          style={{ marginTop: 'var(--space-xl)', padding: 'var(--space-lg) var(--space-2xl)', background: 'var(--color-action)', color: 'white', border: 'none', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: 600 }}
        >
          完成
        </button>
      </div>
    );
  }

  if (orderStatus === 'pending' && orderId) {
    const pkg = PACKAGES.find((p) => p.key === selectedPkg)!;
    return (
      <div style={{ textAlign: 'center', padding: 'var(--space-3xl)' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-3xl)', fontWeight: 700, color: 'var(--color-action)', margin: '0 0 var(--space-sm)' }}>
          ¥{(pkg.priceFen / 100).toFixed(0)}
        </div>
        <div style={{ color: 'var(--color-text-muted)', marginBottom: 'var(--space-2xl)' }}>
          {pkg.credits.toLocaleString()} 点 · {pkg.label}
        </div>
        <div style={{ padding: 'var(--space-xl)', background: 'var(--color-surface-2)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-xl)' }}>
          订单 #{orderId.slice(0, 8)} · 等待支付...
          <br />
          <span style={{ fontSize: 'var(--text-xs)' }}>（支付集成即将上线 · 当前为骨架模式）</span>
        </div>
        <button
          onClick={() => { setOrderStatus('idle'); setOrderId(null); }}
          style={{ padding: 'var(--space-lg) var(--space-2xl)', background: 'var(--color-surface)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: 500 }}
        >
          取消订单
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Package selection */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-lg)', marginBottom: 'var(--space-2xl)' }}>
        {PACKAGES.map((pkg) => (
          <button
            key={pkg.key}
            onClick={() => setSelectedPkg(pkg.key)}
            style={{
              padding: 'var(--space-xl)', border: `2px solid ${selectedPkg === pkg.key ? 'var(--color-action)' : 'var(--color-border)'}`,
              borderRadius: 'var(--radius-md)', cursor: 'pointer', background: selectedPkg === pkg.key ? 'var(--color-action-soft)' : 'var(--color-surface)',
              textAlign: 'left', transition: 'all 160ms ease',
            }}
          >
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 'var(--text-lg)', color: 'var(--color-ink)' }}>{pkg.label}</div>
            <div style={{ fontSize: 'var(--text-xl)', fontWeight: 700, color: 'var(--color-action)', margin: 'var(--space-sm) 0' }}>¥{(pkg.priceFen / 100).toFixed(0)}</div>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{pkg.credits.toLocaleString()} 点</div>
            {pkg.perks && <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-primary)', marginTop: 'var(--space-sm)' }}>{pkg.perks}</div>}
          </button>
        ))}
      </div>

      {/* Channel selection */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-md)', marginBottom: 'var(--space-2xl)' }}>
        {CHANNELS.map((ch) => (
          <button
            key={ch.key}
            disabled={ch.disabled}
            onClick={() => setSelectedChannel(ch.key)}
            style={{
              padding: 'var(--space-lg)', border: `2px solid ${selectedChannel === ch.key ? 'var(--color-action)' : 'var(--color-border)'}`,
              borderRadius: 'var(--radius-md)', cursor: ch.disabled ? 'not-allowed' : 'pointer',
              background: selectedChannel === ch.key ? 'var(--color-action-soft)' : 'var(--color-surface)',
              opacity: ch.disabled ? 0.5 : 1, textAlign: 'center',
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 'var(--text-md)', color: 'var(--color-ink)' }}>{ch.label}</div>
            {ch.disabled && <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>即将开放</div>}
          </button>
        ))}
      </div>

      {error && <div style={{ color: 'var(--color-error)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-lg)' }}>{error}</div>}

      <button
        onClick={createOrder}
        disabled={orderStatus === 'creating'}
        style={{
          width: '100%', padding: 'var(--space-lg)', background: 'var(--color-action)', color: 'white',
          border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-lg)', fontWeight: 600, cursor: 'pointer',
        }}
      >
        {orderStatus === 'creating' ? '创建中...' : `确认支付 ¥${(PACKAGES.find((p) => p.key === selectedPkg)!.priceFen / 100).toFixed(0)}`}
      </button>
    </div>
  );
}
